import React, { useState, useRef } from 'react';
import { Upload, Video, Plus } from 'lucide-react';
import { cn } from '../lib/utils';

interface DropZoneProps {
  onFoldersSelected: (files: File[]) => void;
}

export const DropZone: React.FC<DropZoneProps> = ({ onFoldersSelected }) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    
    const files = Array.from(e.dataTransfer.files);
    const videoFiles = files.filter(file => file.type.startsWith('video/'));
    
    if (videoFiles.length > 0) {
      onFoldersSelected(videoFiles);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files) {
      const videoFiles = Array.from(files).filter(file => file.type.startsWith('video/'));
      if (videoFiles.length > 0) {
        onFoldersSelected(videoFiles);
      }
    }
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <div
      className={cn(
        "border-2 border-dashed rounded-lg p-8 cursor-pointer transition-all duration-200 group",
        isDragOver 
          ? 'border-primary bg-primary/5 scale-[1.01]' 
          : 'border-border hover:border-primary/50 hover:bg-accent/20'
      )}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      onClick={handleClick}
    >
      <div className="flex flex-col items-center justify-center text-center space-y-6">
        <div className={cn(
          "rounded-full p-4 transition-all duration-200",
          isDragOver ? "bg-primary/10 scale-110" : "bg-secondary group-hover:bg-accent"
        )}>
          <div className="flex items-center justify-center">
            <Video className={cn(
              "h-8 w-8 transition-colors duration-200",
              isDragOver ? "text-primary" : "text-muted-foreground group-hover:text-foreground"
            )} />
            <Plus className={cn(
              "h-4 w-4 -ml-2 mt-2 transition-colors duration-200",
              isDragOver ? "text-primary" : "text-muted-foreground group-hover:text-foreground"
            )} />
          </div>
        </div>
        
        <div className="space-y-2">
          <p className={cn(
            "text-base font-medium transition-colors duration-200",
            isDragOver ? "text-primary" : "text-foreground"
          )}>
            Drop video files here
          </p>
          <p className="text-sm text-muted-foreground">
            Or click to select multiple video files
          </p>
          <p className="text-xs text-muted-foreground/80">
            Recommended: 9:16 aspect ratio • Formats: MP4, MOV, AVI, WebM
          </p>
        </div>
      </div>
      
      <input
        ref={fileInputRef}
        type="file"
        accept="video/*"
        multiple
        className="hidden"
        onChange={handleFileSelect}
      />
    </div>
  );
};